import { useState, useEffect, useCallback } from "react";
import { Shield, LogOut, Users, Building2, Store, FileText, AlertTriangle, Trash2, ChevronRight, ArrowLeft, RefreshCw, Loader2, Search, Package, Clock, Download, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;

/* ─── Login Screen ──────────────────────────────────────────── */
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      localStorage.setItem("sa_token", res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Access denied");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-amber-500" />
          </div>
          <h1 className="text-xl font-semibold text-white">Super Admin</h1>
          <p className="text-slate-500 text-sm mt-1">EasyWaybill Control Plane</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div>
            <label className="text-slate-400 text-xs font-medium mb-1 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder:text-slate-600"
              placeholder="admin@company.com"
              required
              data-testid="sa-login-email"
            />
          </div>
          <div>
            <label className="text-slate-400 text-xs font-medium mb-1 block">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 placeholder:text-slate-600"
              placeholder="Enter password"
              required
              data-testid="sa-login-password"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
            data-testid="sa-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Authenticate"}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ─── Stat Card ──────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

/* ─── Org Detail Panel ──────────────────────────────────────── */
const OrgDetail = ({ orgId, token, onBack, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/organizations/${orgId}`, { headers })
      .then(res => setData(res.data))
      .catch(err => console.error("Failed to load org detail:", err))
      .finally(() => setLoading(false));
    // Fetch customer data
    axios.get(`${API}/organizations/${orgId}/customers`, { headers })
      .then(res => setCustomerData(res.data))
      .catch(() => {});
  }, [orgId, token]);

  const handleDeleteOrg = async () => {
    if (!window.confirm("DANGER: This will permanently delete this organization and ALL its data (users, settings, waybills, courier config). Continue?")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/organizations/${orgId}`, { headers });
      onRefresh();
      onBack();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
    setDeleting(false);
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await axios.delete(`${API}/users/${email}`, { headers });
      const res = await axios.get(`${API}/organizations/${orgId}`, { headers });
      setData(res.data);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  if (!data) return <p className="text-slate-500 text-sm p-4">Organization not found.</p>;

  const { organization: org, users, settings, courier_config, waybill_count, customer_emails } = data;

  return (
    <div className="space-y-6" data-testid="org-detail-panel">
      <button onClick={onBack} className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm transition-colors" data-testid="org-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to list
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">{org.name}</h2>
          <p className="text-xs text-slate-500 font-mono mt-0.5">{org.id}</p>
          <p className="text-xs text-slate-500 mt-1">Created: {new Date(org.created_at).toLocaleDateString()}</p>
        </div>
        <button
          onClick={handleDeleteOrg}
          disabled={deleting}
          className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
          data-testid="org-delete-btn"
        >
          {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Trash2 className="w-3 h-3 inline mr-1" />Delete Org</>}
        </button>
      </div>

      {/* Shop Connection */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><Store className="w-4 h-4" /> Shopify Connection</h3>
        {settings ? (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <span className="text-slate-500">Shop:</span>
            <span className="text-white font-mono">{settings.shopify_shop || "—"}</span>
            <span className="text-slate-500">Connected:</span>
            <span className={settings.shopify_connected ? "text-green-400" : "text-red-400"}>{settings.shopify_connected ? "Yes" : "No"}</span>
          </div>
        ) : (
          <p className="text-slate-500 text-xs">No Shopify connection</p>
        )}
      </div>

      {/* Courier Config */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Courier Config</h3>
        {courier_config ? (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <span className="text-slate-500">Courier:</span>
            <span className="text-white">{courier_config.courier || "first_freight"}</span>
            <span className="text-slate-500">Account:</span>
            <span className="text-white font-mono">{courier_config.account_number || "—"}</span>
          </div>
        ) : (
          <p className="text-slate-500 text-xs">No courier configured</p>
        )}
      </div>

      {/* Stats */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-2">Waybills</h3>
        <p className="text-2xl font-bold text-white">{waybill_count}</p>
      </div>

      {/* Users */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Users ({users.length})</h3>
        {users.length === 0 ? (
          <p className="text-slate-500 text-xs">No users in this organization</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.email} className="flex items-center justify-between bg-slate-950 rounded-lg px-3 py-2">
                <div>
                  <p className="text-white text-sm">{u.name || u.email}</p>
                  <p className="text-slate-500 text-xs">{u.email}</p>
                </div>
                <button
                  onClick={() => handleDeleteUser(u.email)}
                  className="text-red-400 hover:text-red-300 p-1"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shopify Customers — SupplyWatch style */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Shopify Customers
        </h3>

        {/* Status row */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${settings?.shopify_connected ? "bg-green-500" : "bg-red-500"}`} />
            <span className="text-xs text-slate-400">{settings?.shopify_connected ? "Token Valid" : "Token Expired"}</span>
          </div>
          <div className="text-xs text-slate-500">
            {customerData?.total > 0
              ? <><span className="text-emerald-400 font-medium">{customerData.total.toLocaleString()}</span> customers synced</>
              : "No customer data synced yet"
            }
          </div>
          {customerData?.last_sync && (
            <span className="text-[10px] text-slate-600">Last sync: {new Date(customerData.last_sync).toLocaleString("en-ZA")}</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={async () => {
              if (!customerData || customerData.total === 0) {
                alert("No customer data to download. Sync first.");
                return;
              }
              window.open(`${API}/organizations/${orgId}/customers/download?session=${token}`, "_blank");
            }}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:bg-slate-700 transition-colors"
            data-testid="download-customers-csv"
          >
            <Download className="w-3.5 h-3.5" /> Download as CSV
          </button>
          <button
            onClick={async () => {
              setSyncing(true);
              try {
                const res = await axios.post(`${API}/organizations/${orgId}/customers/sync`, {}, { headers });
                setCustomerData(prev => ({ ...prev, total: res.data.total, last_sync: new Date().toISOString() }));
                // Refresh customer data
                const fresh = await axios.get(`${API}/organizations/${orgId}/customers`, { headers });
                setCustomerData(fresh.data);
                alert(res.data.message);
              } catch (err) {
                alert(err.response?.data?.detail || "Sync failed");
              }
              setSyncing(false);
            }}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
            data-testid="sync-customers-btn"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? "Syncing..." : "Extract Fresh Data"}
          </button>
        </div>

        {/* Customer sample list */}
        {customerData?.sample?.length > 0 && (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {customerData.sample.map((c) => (
              <div key={c.email || c.shopify_customer_id} className="flex items-center justify-between bg-slate-950 rounded-lg px-3 py-1.5">
                <div className="min-w-0">
                  <p className="text-white text-xs truncate">{c.name || c.email || "—"}</p>
                  <p className="text-slate-500 text-[10px] truncate">{c.email}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-slate-400 text-[10px]">{c.orders_count} orders</p>
                  {c.city && <p className="text-slate-600 text-[10px]">{c.city}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Dashboard ────────────────────────────────────────── */
const AdminDashboard = ({ token, onLogout }) => {
  const [view, setView] = useState("overview"); // overview | orgs | users | shops | orphans | activity
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [orphans, setOrphans] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cleaningOrphans, setCleaningOrphans] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      const [statsRes, orgsRes, usersRes, shopsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: authHeaders }),
        axios.get(`${API}/organizations`, { headers: authHeaders }),
        axios.get(`${API}/users`, { headers: authHeaders }),
        axios.get(`${API}/shops`, { headers: authHeaders }),
      ]);
      setStats(statsRes.data);
      setOrgs(orgsRes.data.organizations || []);
      setUsers(usersRes.data.users || []);
      setShops(shopsRes.data.shops || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("sa_token");
        onLogout();
      }
    }
    setLoading(false);
  }, [token, onLogout]);

  const fetchOrphans = async () => {
    try {
      const res = await axios.get(`${API}/orphans`, { headers });
      setOrphans(res.data);
    } catch (err) {
      console.error("Failed to fetch orphans:", err);
      setOrphans({ orphan_settings: [], orphan_users: [], orphan_settings_count: 0, orphan_users_count: 0 });
    }
  };

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API}/activity?limit=100`, { headers });
      setActivity(res.data);
    } catch (err) {
      console.error("fetchActivity error:", err);
      setActivity({ entries: [], total: 0 });
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCleanOrphans = async () => {
    if (!window.confirm("Delete all orphaned settings and users?")) return;
    setCleaningOrphans(true);
    try {
      await axios.post(`${API}/orphans/cleanup`, {}, { headers });
      await fetchOrphans();
      await fetchAll();
    } catch (err) {
      console.error("Orphan cleanup failed:", err);
      alert(err.response?.data?.detail || "Cleanup failed");
    }
    setCleaningOrphans(false);
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}? If they are the last in their org, the org will also be deleted.`)) return;
    try {
      await axios.delete(`${API}/users/${email}`, { headers });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed");
    }
  };

  const handleDeleteShop = async (domain) => {
    if (!window.confirm(`Remove shop ${domain}?`)) return;
    try {
      await axios.delete(`${API}/shops/${domain}`, { headers });
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/logout`, {}, { headers });
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    localStorage.removeItem("sa_token");
    onLogout();
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  // Filter helpers
  const filteredOrgs = orgs.filter(o =>
    o.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.shop_domain?.toLowerCase().includes(search.toLowerCase()) ||
    o.id?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredShops = shops.filter(s =>
    s.shopify_shop?.toLowerCase().includes(search.toLowerCase())
  );

  // If viewing org detail
  if (selectedOrg) {
    return (
      <div className="min-h-screen bg-slate-950 p-6">
        <div className="max-w-3xl mx-auto">
          <OrgDetail orgId={selectedOrg} token={token} onBack={() => setSelectedOrg(null)} onRefresh={fetchAll} />
        </div>
      </div>
    );
  }

  const navItems = [
    { id: "overview", label: "Overview", icon: Shield },
    { id: "orgs", label: `Orgs (${orgs.length})`, icon: Building2 },
    { id: "users", label: `Users (${users.length})`, icon: Users },
    { id: "shops", label: `Shops (${shops.length})`, icon: Store },
    { id: "orphans", label: "Orphans", icon: AlertTriangle },
    { id: "activity", label: "Activity", icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="sa-dashboard">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white">Super Admin</h1>
              <p className="text-[10px] text-slate-500">EasyWaybill Control Plane</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchAll} className="p-2 text-slate-500 hover:text-white transition-colors" data-testid="sa-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 text-slate-400 hover:text-white text-xs transition-colors" data-testid="sa-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Nav Tabs */}
        <nav className="flex gap-1 mb-6 bg-slate-900 rounded-xl p-1 border border-slate-800">
          {navItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setView(id); setSearch(""); if (id === "orphans") fetchOrphans(); if (id === "activity") fetchActivity(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                view === id ? "bg-amber-500/10 text-amber-500 border border-amber-500/30" : "text-slate-500 hover:text-white"
              }`}
              data-testid={`sa-nav-${id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </nav>

        {/* Overview */}
        {view === "overview" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard icon={Building2} label="Organizations" value={stats.total_organizations} color="bg-blue-500/10 text-blue-400" />
              <StatCard icon={Users} label="Users" value={stats.total_users} color="bg-emerald-500/10 text-emerald-400" />
              <StatCard icon={Store} label="Connected Shops" value={stats.total_connected_shops} color="bg-violet-500/10 text-violet-400" />
              <StatCard icon={FileText} label="Waybills" value={stats.total_waybills} color="bg-amber-500/10 text-amber-400" />
              <StatCard icon={Package} label="Courier Configs" value={stats.total_courier_configs} color="bg-pink-500/10 text-pink-400" />
            </div>

            {/* Quick org list */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-300">All Organizations</h3>
                <button onClick={() => setView("orgs")} className="text-xs text-amber-500 hover:text-amber-400">View all</button>
              </div>
              {orgs.length === 0 ? (
                <p className="text-slate-500 text-sm p-4">No organizations yet</p>
              ) : (
                <div className="divide-y divide-slate-800">
                  {orgs.slice(0, 5).map(org => (
                    <button
                      key={org.id}
                      onClick={() => setSelectedOrg(org.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors text-left"
                      data-testid={`org-row-${org.id}`}
                    >
                      <div>
                        <p className="text-white text-sm">{org.name}</p>
                        <p className="text-slate-500 text-xs mt-0.5">
                          {org.shop_domain || "No shop"} · {org.user_count} user{org.user_count !== 1 ? "s" : ""}
                          {org.courier_configured && <span className="text-green-500 ml-2">Courier OK</span>}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orgs List */}
        {view === "orgs" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search organizations..."
                className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none placeholder:text-slate-600"
                data-testid="sa-org-search"
              />
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
              {filteredOrgs.length === 0 ? (
                <p className="text-slate-500 text-sm p-6 text-center">No organizations found</p>
              ) : filteredOrgs.map(org => (
                <button
                  key={org.id}
                  onClick={() => setSelectedOrg(org.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors text-left"
                  data-testid={`org-row-${org.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{org.name}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="text-slate-500 text-xs">{org.shop_domain || "No shop linked"}</span>
                      <span className="text-slate-600 text-xs">{org.user_count} user{org.user_count !== 1 ? "s" : ""}</span>
                      {org.shopify_connected && <span className="text-xs text-green-500">Shopify</span>}
                      {org.courier_configured && <span className="text-xs text-blue-400">Courier</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Users List */}
        {view === "users" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none placeholder:text-slate-600"
                data-testid="sa-user-search"
              />
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
              {filteredUsers.length === 0 ? (
                <p className="text-slate-500 text-sm p-6 text-center">No users found</p>
              ) : filteredUsers.map(u => (
                <div key={u.email} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{u.name || u.email}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-slate-500 text-xs">{u.email}</span>
                      <span className="text-slate-600 text-xs font-mono">{u.organization_id ? `Org: ${u.organization_id.slice(0, 8)}...` : "No org"}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteUser(u.email)} className="p-2 text-red-400/50 hover:text-red-400 transition-colors" data-testid={`sa-delete-user-${u.email}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shops List */}
        {view === "shops" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search shops..."
                className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none placeholder:text-slate-600"
                data-testid="sa-shop-search"
              />
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
              {filteredShops.length === 0 ? (
                <p className="text-slate-500 text-sm p-6 text-center">No shops found</p>
              ) : filteredShops.map(s => (
                <div key={s.shopify_shop || s.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-mono truncate">{s.shopify_shop || "Unknown"}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className={`text-xs ${s.shopify_connected ? "text-green-500" : "text-red-400"}`}>
                        {s.shopify_connected ? "Connected" : "Disconnected"}
                      </span>
                      <span className="text-slate-600 text-xs">{s.organization_id ? `Org: ${s.organization_id.slice(0, 8)}...` : "Orphan"}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteShop(s.shopify_shop)} className="p-2 text-red-400/50 hover:text-red-400 transition-colors" data-testid={`sa-delete-shop-${s.shopify_shop}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orphans */}
        {view === "orphans" && (
          <div className="space-y-4">
            {!orphans ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                    <p className="text-xs text-slate-500 mb-1">Orphan Settings</p>
                    <p className="text-2xl font-bold text-white">{orphans.orphan_settings_count}</p>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                    <p className="text-xs text-slate-500 mb-1">Orphan Users</p>
                    <p className="text-2xl font-bold text-white">{orphans.orphan_users_count}</p>
                  </div>
                </div>

                {(orphans.orphan_settings_count > 0 || orphans.orphan_users_count > 0) ? (
                  <>
                    {orphans.orphan_settings.length > 0 && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-300 mb-2">Orphan Settings (shops without org)</h3>
                        <div className="space-y-1">
                          {orphans.orphan_settings.map((s, i) => (
                            <p key={s.shopify_shop || `orphan-setting-${i}`} className="text-xs text-slate-400 font-mono">{s.shopify_shop || "unknown"}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {orphans.orphan_users.length > 0 && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-300 mb-2">Orphan Users (no valid org)</h3>
                        <div className="space-y-1">
                          {orphans.orphan_users.map((u, i) => (
                            <p key={u.email || `orphan-user-${i}`} className="text-xs text-slate-400">{u.email} — org: {u.organization_id || "none"}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={handleCleanOrphans}
                      disabled={cleaningOrphans}
                      className="w-full bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="sa-clean-orphans-btn"
                    >
                      {cleaningOrphans ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Clean Up All Orphans
                    </button>
                  </>
                ) : (
                  <div className="bg-slate-900 border border-green-500/20 rounded-xl p-6 text-center">
                    <p className="text-green-400 text-sm font-medium">All clean — no orphaned data found</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Activity Log */}
        {view === "activity" && (
          <div className="space-y-4">
            {!activity ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
            ) : activity.entries.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
                <Clock className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No activity recorded yet</p>
                <p className="text-slate-600 text-xs mt-1">Actions like logins, deletions, and cleanups will appear here</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">{activity.total} total event{activity.total !== 1 ? "s" : ""}</p>
                  <button onClick={fetchActivity} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1" data-testid="sa-activity-refresh">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl divide-y divide-slate-800">
                  {activity.entries.map((entry, i) => {
                    const actionColors = {
                      login: "text-emerald-400 bg-emerald-500/10",
                      logout: "text-slate-400 bg-slate-500/10",
                      delete_user: "text-red-400 bg-red-500/10",
                      delete_user_and_org: "text-red-400 bg-red-500/10",
                      delete_shop: "text-orange-400 bg-orange-500/10",
                      delete_organization: "text-red-400 bg-red-500/10",
                      cleanup_orphans: "text-amber-400 bg-amber-500/10",
                    };
                    const actionLabels = {
                      login: "Logged in",
                      logout: "Logged out",
                      delete_user: "Deleted user",
                      delete_user_and_org: "Deleted user + org",
                      delete_shop: "Removed shop",
                      delete_organization: "Deleted organization",
                      cleanup_orphans: "Cleaned orphans",
                    };
                    const colorClass = actionColors[entry.action] || "text-slate-400 bg-slate-500/10";
                    const label = actionLabels[entry.action] || entry.action;
                    const ts = new Date(entry.ts);
                    const timeStr = ts.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) + " " + ts.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });

                    return (
                      <div key={`${entry.ts}-${entry.action}-${i}`} className="px-4 py-3 flex items-start gap-3" data-testid={`activity-entry-${i}`}>
                        <div className={`mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${colorClass}`}>
                          {label}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-300 text-xs font-mono truncate">{entry.actor}</span>
                          </div>
                          {entry.target && (
                            <p className="text-slate-500 text-xs mt-0.5 truncate">Target: <span className="text-slate-400 font-mono">{entry.target}</span></p>
                          )}
                          {entry.detail && (
                            <p className="text-slate-600 text-xs mt-0.5 truncate">{entry.detail}</p>
                          )}
                        </div>
                        <span className="text-slate-600 text-[10px] whitespace-nowrap flex-shrink-0">{timeStr}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Root ───────────────────────────────────────────────────── */
const SuperAdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("sa_token"));

  if (!token) {
    return <AdminLogin onLogin={(t) => setToken(t)} />;
  }

  return <AdminDashboard token={token} onLogout={() => setToken(null)} />;
};

export default SuperAdminPage;
